import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Sparkles } from "lucide-react";
import { logger } from '@/utils/logger';

interface StepOneProps {
  onNext: (avatar: string, color: string) => void;
}

const avatars = ["🦁", "🐼", "🦊", "🐸", "🦄", "🐯", "🐨", "🐷"];
const colors = [
  { name: "Purple", value: "#9b87f5" },
  { name: "Blue", value: "#3b82f6" },
  { name: "Green", value: "#10b981" },
  { name: "Pink", value: "#ec4899" },
  { name: "Orange", value: "#f97316" },
  { name: "Red", value: "#ef4444" },
];

export function StepOne({ onNext }: StepOneProps) {
  const [selectedAvatar, setSelectedAvatar] = useState(avatars[0]);
  const [selectedColor, setSelectedColor] = useState(colors[0].value);

  return (
    <div className="space-y-8 text-center">
      <div className="space-y-3">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-[#F97316] to-[#D946EF] mb-2">
          <Sparkles className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-3xl font-bold">Welcome! Let's Make This Yours! 🎨</h2>
        <p className="text-muted-foreground text-lg">Pick an avatar and your favorite color to get started</p>
      </div>

      <div className="space-y-6">
        <div className="space-y-3">
          <label className="text-lg font-semibold block">Choose Your Avatar</label>
          <div className="grid grid-cols-4 gap-3">
            {avatars.map((avatar) => (
              <button
                type="button"
                key={avatar}
                onClick={() => setSelectedAvatar(avatar)}
                className={`text-5xl p-4 rounded-2xl border-4 transition-all hover:scale-110 ${selectedAvatar === avatar ? "border-gradient bg-gradient-to-br from-[#F97316]/20 to-[#D946EF]/20 scale-110 shadow-lg" : "border-border hover:border-muted-foreground"}`}
              >
                {avatar}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <label className="text-lg font-semibold block">Pick Your Color</label>
          <div className="grid grid-cols-3 gap-3">
            {colors.map((color) => (
              <button
                type="button"
                key={color.value}
                onClick={() => { 
                  setSelectedColor(color.value);
                  logger.debug("🧭 Onboarding: Color selected, auto-advancing", { color: color.value, avatar: selectedAvatar });
                  onNext(selectedAvatar, color.value);
                }}
                className={`p-4 rounded-xl border-4 transition-all hover:scale-105 font-medium ${selectedColor === color.value ? "border-foreground scale-105 shadow-lg" : "border-border hover:border-muted-foreground"}`}
                style={{ backgroundColor: `${color.value}20`, color: color.value }}
              >
                {color.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      <Button type="button" size="lg" className="w-full text-lg bg-gradient-to-r from-[#F97316] to-[#D946EF] hover:opacity-90" onClick={() => onNext(selectedAvatar, selectedColor)}>
        Next Step →
      </Button>
    </div>
  );
}
